import { useEffect, useState } from 'react';
import { createElectronicBill } from '@/services/dian';
import { useCustomMutation } from '@/hooks/useCustomMutation';
import { useForm } from 'react-hook-form';
import { useCreateCustomerModal } from '@/modules/customer/modals/create';
import { useSnackbar } from 'notistack';
import useDebounce from '@/hooks/useDebounce';
import { GenerateElectornicBillForm } from '../components/bill-electronic';
import { useCustomers } from './useCustomer';

export const useGenerateElectronicBill = (orderId: string) => {
  const [showAlert, setShowAlert] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');

  const [query, setQuery] = useState('');
  const [offset] = useState<number>(0);
  const [pageSize] = useState<number>(1);

  const methods = useForm<GenerateElectornicBillForm>({});
  const { enqueueSnackbar } = useSnackbar();
  const { modal, actionModal } = useCreateCustomerModal(inputValue, setCustomerId);
  const isConsumer = methods.watch('isConsumer');

  const debouncedSetQuery = useDebounce((value: string) => {
    setQuery(value);
  }, 500);

  const { mutateAsync } = useCustomMutation(['electronicBill', 'created'], createElectronicBill, {
    callbackError: () => {
      handlerCloseAlert();
      enqueueSnackbar('Error al generar factura!', { variant: 'error' });
    },
  });

  useEffect(() => {
    if (isConsumer) {
      methods.setValue('customer_id', undefined);
      setCustomerId('');
      setInputValue('');
    }
  }, [isConsumer, methods]);

  const { data: { data: customerOptions } = { data: [] } } = useCustomers(query, offset, pageSize);
  const handlerShowAlert = () => setShowAlert(true);
  const handlerCloseAlert = () => setShowAlert(false);

  const handlerGenerateBill = () => {
    handlerShowAlert();
  };

  const handlerOptionClick = (option: { id: string; name: string }) => {
    setInputValue(option.name);
    debouncedSetQuery(option.name);
    setCustomerId(option.id);
  };

  const handlerOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedSetQuery(e.target.value);
  };

  const handlerCreateOption = (value?: string | number | readonly string[]) => {
    setInputValue(String(value));
    actionModal();
  };

  const onSubmit = async (dataForm: GenerateElectornicBillForm) => {
    try {
      const { email_cc_list, isConsumer: isConsumerValue } = dataForm;
      const emailList = email_cc_list || {};
      const payload = {
        orderId,
        customerId,
        isGlobal: isConsumerValue || false,
        cc_list: Object.values(emailList).filter((value) => value),
      };
      const resp = await mutateAsync({
        ...payload,
      });
      setShowAlert(false);
      methods.reset();
      setInputValue('');
      setCustomerId('');
    } catch (error) {
      console.error(error);
    }
  };

  return {
    customerOptions,
    showAlert,
    handlerCloseAlert,
    handlerCreateOption,
    handlerGenerateBill,
    handlerOnChange,
    handlerOptionClick,
    inputValue,
    isConsumer,
    modal,
    methods,
    onSubmit,
  };
};
